/**
 * Active project preview used on /brief and by the isolated prototype route.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Badge,
  Button,
  Image,
  HStack,
  VStack,
  Skeleton,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  MdOutlineWarningAmber,
  MdOutlineAutoAwesome,
  MdOutlineLightbulb,
  MdAccessTime,
  MdArrowForward,
} from "react-icons/md";
import { fetchWithAuth } from "../utils/fetchWithAuth";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return "1d ago";
  return `${days}d ago`;
}

function getCtaStory(cta) {
  if (!cta) return null;

  switch (cta.action) {
    case "set_strategy_status":
      return {
        prompt: "Where did this land?",
        tooltip: "Tell Pabs whether this strategy is still active, still being explored, parked, complete enough, or no longer being pursued.",
      };
    case "open_chat":
      return {
        prompt: "Let’s work this through:",
        tooltip: "Open a conversation with Pabs to think through the unresolved question before taking action.",
      };
    case "navigate":
      return {
        prompt: "I need your move here:",
        tooltip: "Open this strategy and take the concrete next step Pabs has identified.",
      };
    default:
      return {
        prompt: "Your next move:",
        tooltip: "Open this strategy to continue from Pabs’ current recommendation.",
      };
  }
}

// ─── Single card ─────────────────────────────────────────────────────────────

function PlanCard({ plan, index, onImageGenerated }) {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(plan.pulse?.image_url || null);
  const [imgError, setImgError] = useState("");
  const [phrase, setPhrase] = useState(null);

  const firstCta = plan.pulse?.cta_actions?.[0];
  const ctaStory = getCtaStory(firstCta);
  const pulseAge = timeAgo(plan.pulse?.created_at);
  // Randomize which corner is straight based on card position
  const straightCorner = index % 2 === 0 ? "bottomRight" : "bottomLeft";
  const imgRadius = {
    borderTopLeftRadius: "3xl",
    borderTopRightRadius: "3xl",
    borderBottomLeftRadius: "0",
    borderBottomRightRadius: "0",
  };
  const contentRadius = {
    borderBottomLeftRadius: straightCorner === "bottomLeft" ? "0" : "3xl",
    borderBottomRightRadius: straightCorner === "bottomRight" ? "0" : "3xl",
  };

  const handleGenerateImage = async (e) => {
    e.stopPropagation();
    setGenerating(true);
    setImgError("");
    try {
      const res = await fetchWithAuth(`/api/pulse/${plan.id}/image`, {
        method: "POST",
      });
      if (res.ok) {
        const data = await res.json();
        if (data.image_url) {
          setImgError("");
          setImageUrl(data.image_url);
          if (data.phrase) setPhrase(data.phrase);
          if (onImageGenerated) onImageGenerated(plan.id, data.image_url);
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setImgError(data.details || data.error || `Image generation failed (${res.status})`);
      }
    } catch (error) {
      setImgError(error.message || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const cardTitle =
    plan.plan_name ||
    plan.design_challenge?.split(" ").slice(0, 7).join(" ") + "...";

  return (
    <Box
      bg="white"
      borderTopLeftRadius="3xl"
      borderTopRightRadius="3xl"
      borderBottomLeftRadius={straightCorner === "bottomLeft" ? "0" : "3xl"}
      borderBottomRightRadius={straightCorner === "bottomRight" ? "0" : "3xl"}
      cursor="pointer"
      border="1px solid transparent"
      onClick={() => navigate(`/plan/${plan.id}`)}
      _hover={{ bg: "gray.50", borderColor: "black" }}
      transition="background 0.15s ease, border-color 0.15s ease"
      display="flex"
      flexDirection="column"
      h="100%"
    >
      {/* ── Image area ── */}
      <Box
        h="180px"
        bg="white"
        position="relative"
        overflow="hidden"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        {...imgRadius}
      >
        {imageUrl && !imgError ? (
          <>
            <Image
              src={imageUrl}
              alt="Strategy mood"
              objectFit="cover"
              w="100%"
              h="100%"
              onError={() => setImgError("The saved image could not be loaded")}
              style={{ filter: generating ? "blur(6px)" : "none", transition: "filter 0.3s ease" }}
            />
            {/* Shimmer overlay while generating */}
            {generating && (
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(105deg, transparent 40%, whiteAlpha.400 50%, transparent 60%)"
                backgroundSize="200% 100%"
                animation="shimmer 1.4s infinite"
                sx={{
                  "@keyframes shimmer": {
                    "0%": { backgroundPosition: "200% 0" },
                    "100%": { backgroundPosition: "-200% 0" },
                  },
                }}
              />
            )}
            {/* Gradient + phrase at top */}
            {phrase && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                pt={2}
                pb={8}
                px={3}
                bgGradient="linear(to-b, blackAlpha.700, transparent)"
                zIndex={1}
              >
                <Text fontSize="xs" color="whiteAlpha.900" fontStyle="italic" fontWeight="medium" noOfLines={2} pr={16}>
                  "{phrase}"
                </Text>
              </Box>
            )}
          </>
        ) : (
          <VStack spacing={3}>
            <Box
              w="56px"
              h="56px"
              borderRadius="full"
              bg="gray.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={MdOutlineAutoAwesome} boxSize={6} color="gray.300" />
            </Box>
            {plan.pulse ? (
              <>
                {imgError && (
                  <Text fontSize="2xs" color="orange.500" textAlign="center" px={3} noOfLines={3}>
                    {imgError}
                  </Text>
                )}
                <Tooltip
                  label="Turn Pabs’ current emotional read of this strategy into a piece of artwork."
                  hasArrow
                >
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    borderRadius="full"
                    fontSize="xs"
                    isLoading={generating}
                    loadingText="Creating my mood..."
                    onClick={handleGenerateImage}
                  >
                    See how I’m feeling
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Text fontSize="xs" color="gray.400">
                Run a pulse first
              </Text>
            )}
          </VStack>
        )}

        {/* Regenerate — top-right, above gradient */}
        {plan.pulse && imageUrl && (
          <Tooltip
            label="Create a new piece of artwork from Pabs’ current emotional read of this strategy."
            hasArrow
          >
            <Button
              position="absolute"
              top={2}
              right={2}
              size="xs"
              bg="whiteAlpha.900"
              color="gray.800"
              fontSize="2xs"
              px={2}
              borderRadius="full"
              opacity={1}
              isLoading={generating}
              onClick={handleGenerateImage}
              zIndex={2}
              _hover={{ bg: "brand.900", color: "white", opacity: 1 }}
            >
              Reimagine my mood
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* ── Content area — Pabs' read, human move, supporting signals ── */}
      <Flex
        px={6} pt={6} pb={6}
        direction="column"
        gap={3}
        flex={1}
        bg="white"
        {...contentRadius}
      >
        {/* Title — small, secondary */}
        <Text fontWeight="medium" fontSize="xs" color="gray.500" noOfLines={1} textTransform="uppercase" letterSpacing="tight">
          {cardTitle}
        </Text>

        <Text
          fontSize="2xs"
          color="gray.400"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="0.12em"
          mt={1}
        >
          What I’m seeing
        </Text>

        {/* Existing pulse verdict, reframed as Pabs speaking */}
        {plan.pulse?.first_sentence ? (
          <Text fontSize="sm" color="gray.700" lineHeight="1.6" noOfLines={3} fontWeight="medium">
            {plan.pulse.first_sentence}
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.400" fontStyle="italic">
            I need a pulse before I can give you a useful read.
          </Text>
        )}

        {/* Existing CTA, reframed as the human move Pabs needs */}
        {firstCta && ctaStory && (
          <Box mt={1}>
            <Text
              fontSize="sm"
              color="gray.700"
              lineHeight="1.6"
              fontWeight="medium"
              mb={2}
            >
              {ctaStory.prompt}
            </Text>
            <Tooltip
              label={ctaStory.tooltip}
              hasArrow
              placement="top"
              openDelay={400}
            >
              <Button
                size="sm"
                width="100%"
                bg="brand.900"
                color="white"
                px={4}
                rightIcon={<Icon as={MdArrowForward} boxSize={4} />}
                onClick={(e) => { e.stopPropagation(); navigate(`/plan/${plan.id}`); }}
                fontSize="sm"
                fontWeight="semibold"
                borderRadius="xl"
                justifyContent="space-between"
                _hover={{ bg: "brand.800", transform: "translateY(-1px)" }}
                _active={{ bg: "brand.900", transform: "translateY(0)" }}
                transition="all 0.15s ease"
              >
                {firstCta.label}
              </Button>
            </Tooltip>
          </Box>
        )}

        {/* ── Compact signals + quiet pulse date ── */}
        <HStack spacing={2} flexWrap="wrap" mt="auto" pt={2}>
          <HStack spacing={2} flexWrap="wrap">
            {plan.high_blocker_count > 0 && (
              <Tooltip
                label={`${plan.high_blocker_count} high-priority strategic blocker${plan.high_blocker_count > 1 ? "s still need" : " still needs"} a human decision or resolution.`}
                hasArrow
                placement="top"
              >
                <HStack
                  spacing={1}
                  px={2.5}
                  py={1}
                  bg="orange.50"
                  borderRadius="full"
                  color="orange.600"
                >
                  <Icon as={MdOutlineWarningAmber} boxSize={3.5} />
                  <Text fontSize="2xs" fontWeight="semibold">
                    {plan.high_blocker_count} blocker{plan.high_blocker_count > 1 ? "s" : ""}
                  </Text>
                </HStack>
              </Tooltip>
            )}
            {plan.pending_learnings_count > 0 && (
              <Tooltip
                label={`${plan.pending_learnings_count} evaluated learning${plan.pending_learnings_count > 1 ? "s are" : " is"} waiting for your review before changing the strategy.`}
                hasArrow
                placement="top"
              >
                <HStack
                  spacing={1}
                  px={2.5}
                  py={1}
                  bg="blue.50"
                  borderRadius="full"
                  color="blue.600"
                >
                  <Icon as={MdOutlineLightbulb} boxSize={3.5} />
                  <Text fontSize="2xs" fontWeight="semibold">
                    {plan.pending_learnings_count} learning{plan.pending_learnings_count > 1 ? "s" : ""}
                  </Text>
                </HStack>
              </Tooltip>
            )}
            {plan.high_blocker_count === 0 && plan.pending_learnings_count === 0 && (
              <Tooltip label="Pabs has no high-priority blockers or evaluated learnings waiting for you." hasArrow>
                <Box px={2.5} py={1} bg="gray.100" borderRadius="full">
                  <Text fontSize="2xs" color="gray.500" fontWeight="semibold">
                    Nothing waiting
                  </Text>
                </Box>
              </Tooltip>
            )}
          </HStack>
          {pulseAge && (
            <Tooltip label={`Pabs last reassessed this strategy ${pulseAge}.`} hasArrow placement="top">
              <HStack spacing={1} ml="auto">
                <Icon as={MdAccessTime} boxSize={3} color="gray.400" />
                <Text fontSize="2xs" color="gray.500">{pulseAge}</Text>
              </HStack>
            </Tooltip>
          )}
        </HStack>
      </Flex>
    </Box>
  );
}

// ─── Prototype page ───────────────────────────────────────────────────────────

export default function BriefProjectsPreview({
  embedded = false,
  onAvailabilityChange,
}) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth("/api/plans/digest");
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        setPlans(data.plans || []);
        setRawData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    onAvailabilityChange?.({
      loading,
      hasProjects: !loading && !error && plans.length > 0,
    });
  }, [error, loading, onAvailabilityChange, plans.length]);

  const handleImageGenerated = (planId, url) => {
    setPlans((prev) =>
      prev.map((p) =>
        p.id === planId ? { ...p, pulse: { ...p.pulse, image_url: url } } : p
      )
    );
  };

  if (embedded && !loading && (error || plans.length === 0)) {
    return null;
  }

  return (
    <Box
      minH={embedded ? "auto" : "100vh"}
      bg="#f0f0f0"
      p={embedded ? 0 : { base: 4, md: 8 }}
      px={embedded ? { base: 4, md: 8 } : undefined}
      pb={embedded ? { base: 8, md: 12 } : undefined}
      mt={embedded ? { base: 8, md: 12 } : 0}
    >
      <Box w="100%">

        {/* Header */}
        <Flex align="center" justify="center" mb={8} position="relative">
          <VStack align="center" spacing={1} textAlign="center">
            {!embedded && (
              <HStack>
                <Badge colorScheme="purple" borderRadius="full" px={2} fontSize="2xs" fontWeight="bold">
                  PROTO
                </Badge>
                <Text fontSize="xs" color="gray.400">
                  /proto/brief-cards
                </Text>
              </HStack>
            )}
            {!loading && (
              <Heading size="md" color="gray.800">
                I’m watching these. I’ll bring you in when I need a decision.
              </Heading>
            )}
          </VStack>
          {!embedded && (
            <Button
              position="absolute"
              right={0}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? "Hide" : "Show"} raw data
            </Button>
          )}
        </Flex>

        {/* Cards */}
        {loading && (
          <Flex gap={4} flexWrap="nowrap" justify="center">
            {[1, 2, 3].map((i) => (
              <Box key={i} flex="0 0 300px" w="300px" borderRadius="2xl" overflow="hidden">
                <Skeleton h="180px" />
                <Box bg="white" p={4}>
                  <Skeleton h={4} mb={2} />
                  <Skeleton h={3} mb={1} />
                  <Skeleton h={3} w="60%" mb={3} />
                  <Skeleton h={3} w="40%" />
                </Box>
              </Box>
            ))}
          </Flex>
        )}

        {error && (
          <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="xl" p={4}>
            <Text color="red.600" fontSize="sm">
              Error: {error}
            </Text>
          </Box>
        )}

        {!embedded && !loading && !error && plans.length === 0 && (
          <Text color="gray.500" fontSize="sm">
            No projects yet.
          </Text>
        )}

        {!loading && !error && plans.length > 0 && (
          <Box
            overflowX="auto"
            mx="auto"
            sx={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
          >
            <Flex gap={4} flexWrap="nowrap" align="stretch" justify="center" minW="min-content" mx="auto">
              {plans.map((plan, i) => (
                <Box key={plan.id} flex="0 0 300px" w="300px">
                  <PlanCard plan={plan} index={i} onImageGenerated={handleImageGenerated} />
                </Box>
              ))}
            </Flex>
          </Box>
        )}

        {/* Raw data inspector */}
        {!embedded && showRaw && rawData && (
          <Box
            mt={8}
            p={4}
            bg="gray.800"
            borderRadius="xl"
            maxH="400px"
            overflow="auto"
          >
            <Text fontSize="2xs" color="gray.400" fontFamily="mono" whiteSpace="pre-wrap">
              {JSON.stringify(rawData, null, 2)}
            </Text>
          </Box>
        )}

        {/* Proto notes */}
        {!embedded && (
        <Box
          mt={8}
          p={4}
          bg="white"
          borderRadius="xl"
          border="1px solid"
          borderColor="gray.200"
        >
          <Text fontSize="2xs" color="gray.400" fontWeight="bold" mb={2} letterSpacing="wider">
            NOTES
          </Text>
          <VStack align="start" spacing={1}>
            {[
              "GET /api/plans/digest — new endpoint in routes/plans.js",
              "POST /api/pulse/:planId/image — new endpoint in routes/pulse.js",
              "Image: GPT-4o-mini extracts symbol concept → gpt-image-1 generates → base64 stored in plan_pulses.image_url",
              "gpt-image-1 returns base64 (no URL). Stored as data URL in DB — production needs S3/Cloudinary.",
              "Blockers parsed from product_strategy_reports.strategic_guidance JSON field",
              "Pending learnings: status='pending' AND evaluation_status='evaluated'",
            ].map((note, i) => (
              <Text key={i} fontSize="xs" color="gray.500">
                {`• ${note}`}
              </Text>
            ))}
          </VStack>
        </Box>
        )}
      </Box>
    </Box>
  );
}
